import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import { useTwoFactor } from '../../contexts/TwoFactorContext';
import LoginSecurityTab from '../../components/admin/LoginSecurityTab';
import {
  User,
  Shield,
  Plus,
  Eye,
  EyeOff,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Mail,
  UserPlus,
  Lock,
  ChevronUp,
  Activity,
  X,
  KeyRound,
  QrCode,
  RotateCcw,
} from 'lucide-react';

interface AdminProfile {
  id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface AdminUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  granted_at: string;
  is_active: boolean;
}

// ─── 2FA Section ──────────────────────────────────────────────────────────────

function TwoFASection() {
  const { is2FAEnabled, setup2FA, verify2FA, disable2FA, qrCodeUrl, manualEntryKey, isLoading, error, check2FAStatus } = useTwoFactor();

  type Flow = 'idle' | 'setup' | 'verify' | 'disabling';
  const [flow, setFlow] = useState<Flow>('idle');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [keyCopied, setKeyCopied] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableSuccess, setDisableSuccess] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(false);

  const handleStartSetup = async () => {
    setFlow('setup');
    setCode('');
    setCodeError('');
    setSetupSuccess(false);
    await setup2FA();
  };

  const handleContinueToVerify = () => {
    setFlow('verify');
    setCode('');
    setCodeError('');
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) { setCodeError('Enter all 6 digits.'); return; }
    setCodeError('');
    const ok = await verify2FA(code, true);
    if (ok) {
      setFlow('idle');
      setSetupSuccess(true);
      check2FAStatus();
      setTimeout(() => setSetupSuccess(false), 4000);
    }
  };

  const handleDisable = async () => {
    setFlow('disabling');
    try {
      await disable2FA();
      setShowDisableConfirm(false);
      setFlow('idle');
      setDisableSuccess(true);
      check2FAStatus();
      setTimeout(() => setDisableSuccess(false), 4000);
    } catch {
      setFlow('idle');
    }
  };

  const handleCopyKey = async () => {
    if (!manualEntryKey) return;
    await navigator.clipboard.writeText(manualEntryKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(v);
    if (codeError) setCodeError('');
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${is2FAEnabled ? 'bg-green-100' : 'bg-amber-50'}`}>
          <Shield className={`w-5 h-5 ${is2FAEnabled ? 'text-green-600' : 'text-amber-500'}`} />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900">Two-Factor Authentication</h2>
          <p className="text-xs text-gray-500">Secure your admin account with a TOTP authenticator app</p>
        </div>
        <div className="ml-auto">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${is2FAEnabled ? 'bg-green-100 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${is2FAEnabled ? 'bg-green-500' : 'bg-amber-400'}`} />
            {is2FAEnabled ? '2FA Active' : 'Not Enabled'}
          </span>
        </div>
      </div>

      <div className="px-6 py-5 space-y-4">
        {/* Status banners */}
        {setupSuccess && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700 font-medium">Two-factor authentication enabled successfully.</p>
          </div>
        )}
        {disableSuccess && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700 font-medium">2FA has been disabled. Your account is less secure.</p>
          </div>
        )}
        {error && flow === 'idle' && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* ── IDLE: not enabled ── */}
        {flow === 'idle' && !is2FAEnabled && (
          <div className="flex items-start justify-between gap-4">
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 flex-1">
              <p className="text-sm font-medium text-amber-900 mb-1">Your account is not protected by 2FA</p>
              <p className="text-xs text-amber-700">Enable two-factor authentication to require a 6-digit code from Google Authenticator on every login.</p>
            </div>
            <button
              onClick={handleStartSetup}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#40E0D0] text-black text-sm font-semibold rounded-lg hover:bg-[#38cfc0] transition-colors flex-shrink-0"
            >
              <QrCode className="w-4 h-4" />
              Set Up 2FA
            </button>
          </div>
        )}

        {/* ── IDLE: already enabled ── */}
        {flow === 'idle' && is2FAEnabled && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex-1">
                <p className="text-sm font-medium text-green-900 mb-1">Your account is protected</p>
                <p className="text-xs text-green-700">A 6-digit code from your authenticator app is required at every login.</p>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                <button
                  onClick={handleStartSetup}
                  className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset / Re-enroll
                </button>
                <button
                  onClick={() => setShowDisableConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 text-sm font-semibold rounded-lg hover:bg-red-50 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Disable 2FA
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SETUP: show QR + manual key ── */}
        {flow === 'setup' && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Step 1 — Scan the QR code</p>
              <button onClick={() => setFlow('idle')} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>

            {isLoading && !qrCodeUrl ? (
              <div className="flex flex-col items-center py-10 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-[#40E0D0]" />
                <p className="text-sm text-gray-500">Generating your QR code...</p>
              </div>
            ) : qrCodeUrl ? (
              <>
                <p className="text-sm text-gray-600">
                  Open <strong>Google Authenticator</strong> (or any TOTP app), tap the <strong>+</strong> button, then scan this code:
                </p>

                {/* QR code */}
                <div className="flex justify-center">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-4 inline-block shadow-sm">
                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-52 h-52" />
                  </div>
                </div>

                {/* Manual entry key */}
                {manualEntryKey && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <KeyRound className="w-3.5 h-3.5" />
                      Can't scan? Enter this key manually
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-gray-900 bg-white border border-gray-200 rounded px-3 py-2 tracking-widest select-all break-all">
                        {manualEntryKey}
                      </code>
                      <button
                        type="button"
                        onClick={handleCopyKey}
                        className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
                      >
                        {keyCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                        {keyCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleContinueToVerify}
                  className="w-full py-2.5 bg-[#40E0D0] text-black text-sm font-semibold rounded-lg hover:bg-[#38cfc0] transition-colors"
                >
                  I've added it — Continue to Verify
                </button>
              </>
            ) : null}
          </div>
        )}

        {/* ── VERIFY: enter 6-digit code ── */}
        {flow === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Step 2 — Verify your code</p>
              <button type="button" onClick={() => setFlow('setup')} className="text-gray-400 hover:text-gray-600 p-1 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-sm text-gray-600">
              Open your authenticator app and enter the <strong>6-digit code</strong> shown for this account:
            </p>

            <div>
              <input
                type="text"
                inputMode="numeric"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                autoFocus
                maxLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-3xl tracking-[0.5em] font-mono text-gray-900 focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
              />
              {(codeError || error) && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {codeError || error}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setFlow('setup')}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="flex-1 py-2.5 bg-[#40E0D0] text-black text-sm font-semibold rounded-lg hover:bg-[#38cfc0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {isLoading ? 'Verifying...' : 'Enable 2FA'}
              </button>
            </div>
          </form>
        )}

        {/* ── DISABLE: confirm modal ── */}
        {showDisableConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Disable Two-Factor Authentication?</h3>
                  <p className="text-xs text-gray-500 mt-0.5">This will make your account less secure.</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                You will only need your password to log in. Anyone who obtains your password will have full access to the admin panel.
              </p>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowDisableConfirm(false)}
                  className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisable}
                  disabled={flow === 'disabling'}
                  className="flex-1 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {flow === 'disabling' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Yes, Disable 2FA
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Change Email Section ─────────────────────────────────────────────────────

function ChangeEmailSection() {
  const { user } = useAuth();
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newEmail.trim()) { setError('Please enter a new email address.'); return; }
    if (newEmail !== confirmEmail) { setError('Email addresses do not match.'); return; }
    if (newEmail === user?.email) { setError('New email must be different from your current email.'); return; }

    setSaving(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ email: newEmail.trim() });
      if (updateError) throw updateError;

      // Also update admin_profiles email
      if (user?.id) {
        await supabase.from('admin_profiles').update({ email: newEmail.trim() }).eq('id', user.id);
      }

      setSuccess('Email updated. If your Supabase project requires confirmation, check your inbox to confirm the change.');
      setNewEmail('');
      setConfirmEmail('');
    } catch (err: any) {
      setError(err.message || 'Failed to update email.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
          <Mail className="w-5 h-5 text-gray-500" />
        </div>
        <div>
          <h2 className="font-semibold text-gray-900">Change Email Address</h2>
          <p className="text-xs text-gray-500">Update the email used to log in to the admin panel</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <p className="text-xs text-gray-400 font-medium">Current email</p>
            <p className="text-sm text-gray-700 font-medium">{user?.email || '—'}</p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Email Address</label>
          <input
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="new@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Email</label>
          <input
            type="email"
            value={confirmEmail}
            onChange={e => setConfirmEmail(e.target.value)}
            placeholder="new@example.com again"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">{success}</p>
          </div>
        )}

        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 bg-[#40E0D0] text-black font-semibold text-sm rounded-lg hover:bg-[#38cfc0] transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Update Email
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AccountSettings() {
  const { user } = useAuth();
  useTwoFactor(); // Ensures TwoFactorContext is connected; status is managed automatically by the context.

  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [displayName, setDisplayName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminFirst, setNewAdminFirst] = useState('');
  const [newAdminLast, setNewAdminLast] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [generatingPassword, setGeneratingPassword] = useState(false);

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    setProfileLoading(true);
    try {
      const { data } = await supabase
        .from('admin_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        setProfile(data);
        setDisplayName(data.display_name || '');
        setFirstName(data.first_name || '');
        setLastName(data.last_name || '');
      } else {
        const email = user.email || '';
        setDisplayName(email.split('@')[0]);
        setFirstName('');
        setLastName('');
      }
    } finally {
      setProfileLoading(false);
    }
    // Depend on user.id (primitive) not user (object) to avoid spurious re-runs
    // on every auth event that produces a new user object reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadAdminUsers = useCallback(async () => {
    setAdminsLoading(true);
    try {
      const { data } = await supabase
        .from('user_roles')
        .select('user_id, granted_at, is_active')
        .eq('role', 'admin')
        .order('granted_at', { ascending: false });

      if (data && data.length > 0) {
        const userIds = data.map((r: any) => r.user_id);
        const { data: profiles } = await supabase
          .from('users')
          .select('id, email, first_name, last_name')
          .in('id', userIds);

        const merged: AdminUser[] = data.map((role: any) => {
          const p = profiles?.find((u: any) => u.id === role.user_id);
          return {
            id: role.user_id,
            email: p?.email || '',
            first_name: p?.first_name || '',
            last_name: p?.last_name || '',
            granted_at: role.granted_at,
            is_active: role.is_active,
          };
        });
        setAdminUsers(merged);
      } else {
        setAdminUsers([]);
      }
    } finally {
      setAdminsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadAdminUsers();
  }, [loadProfile, loadAdminUsers]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess(false);

    try {
      const upsertData = {
        id: user.id,
        display_name: displayName || `${firstName} ${lastName}`.trim() || user.email,
        first_name: firstName,
        last_name: lastName,
        email: user.email || '',
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('admin_profiles')
        .upsert(upsertData, { onConflict: 'id' });

      if (error) throw error;

      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
      loadProfile();
    } catch (err: any) {
      setProfileError(err.message || 'Failed to save profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleGeneratePassword = async () => {
    setGeneratingPassword(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user/generate-password`,
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );
      const data = await response.json();
      setGeneratedPassword(data.password || '');
      setShowPassword(true);
    } catch {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&*';
      let pwd = '';
      const arr = new Uint32Array(16);
      crypto.getRandomValues(arr);
      arr.forEach(n => { pwd += chars[n % chars.length]; });
      setGeneratedPassword(pwd);
      setShowPassword(true);
    } finally {
      setGeneratingPassword(false);
    }
  };

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(generatedPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!generatedPassword) {
      setCreateError('Please generate a password first.');
      return;
    }
    setCreatingAdmin(true);
    setCreateError('');
    setCreateSuccess('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-create-user`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
            'Content-Type': 'application/json',
            Apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            email: newAdminEmail,
            firstName: newAdminFirst,
            lastName: newAdminLast,
            generatedPassword,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setCreateError(data.error || 'Failed to create admin user');
        return;
      }

      setCreateSuccess(`Admin account created for ${newAdminFirst} ${newAdminLast} (${newAdminEmail}). Share the password securely.`);
      setNewAdminEmail('');
      setNewAdminFirst('');
      setNewAdminLast('');
      setGeneratedPassword('');
      setShowCreateForm(false);
      loadAdminUsers();
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create admin user');
    } finally {
      setCreatingAdmin(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-500 mt-1 text-sm">Manage your admin profile, security, and team access.</p>
        </div>

        {/* ── Profile ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Profile Information</h2>
              <p className="text-xs text-gray-500">Your name and display information</p>
            </div>
          </div>

          <form onSubmit={handleSaveProfile} className="px-6 py-5 space-y-4">
            {profileLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-4 pb-4 border-b border-gray-50">
                  <div className="w-14 h-14 rounded-full bg-[#40E0D0]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-[#40E0D0]">
                      {(displayName || user?.email || 'A')[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{displayName || user?.email}</p>
                    <p className="text-sm text-gray-500">{user?.email}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none"
                    placeholder="How your name appears in the admin panel"
                  />
                </div>

                {profileError && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700">{profileError}</p>
                  </div>
                )}

                {profileSuccess && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <p className="text-sm text-green-700">Profile saved successfully.</p>
                  </div>
                )}

                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="flex items-center gap-2 px-5 py-2 bg-[#40E0D0] text-black font-semibold text-sm rounded-lg hover:bg-[#38cfc0] transition-colors disabled:opacity-50"
                  >
                    {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Save Profile
                  </button>
                </div>
              </>
            )}
          </form>
        </div>

        {/* ── Security: Change Email ── */}
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 px-1">Security</h2>
          <div className="space-y-4">
            <ChangeEmailSection />
            <TwoFASection />
          </div>
        </div>

        {/* ── Admin Users ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
              <AdminUsersIcon className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Admin Users</h2>
              <p className="text-xs text-gray-500">All accounts with administrator access</p>
            </div>
            <button
              onClick={() => { setShowCreateForm(!showCreateForm); setCreateError(''); setCreateSuccess(''); }}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
            >
              {showCreateForm ? <ChevronUp className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showCreateForm ? 'Cancel' : 'New Admin'}
            </button>
          </div>

          {showCreateForm && (
            <div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-[#40E0D0]" />
                Create New Admin Account
              </h3>

              {createError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">{createError}</p>
                </div>
              )}
              {createSuccess && (
                <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">{createSuccess}</p>
                </div>
              )}

              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                    <input type="text" value={newAdminFirst} onChange={e => setNewAdminFirst(e.target.value)} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none bg-white" placeholder="First name" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                    <input type="text" value={newAdminLast} onChange={e => setNewAdminLast(e.target.value)} required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none bg-white" placeholder="Last name" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" value={newAdminEmail} onChange={e => setNewAdminEmail(e.target.value)} required
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none bg-white" placeholder="admin@example.com" />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Generated Password</label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type={showPassword ? 'text' : 'password'} value={generatedPassword} readOnly
                        className="w-full pl-9 pr-10 py-2 border border-gray-300 rounded-lg text-sm bg-white font-mono text-gray-800"
                        placeholder="Click Generate to create a secure password" />
                      {generatedPassword && (
                        <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                    <button type="button" onClick={handleGeneratePassword} disabled={generatingPassword}
                      className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap">
                      {generatingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Generate
                    </button>
                    {generatedPassword && (
                      <button type="button" onClick={handleCopyPassword}
                        className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                        {passwordCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                  {generatedPassword && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Copy this password now — it cannot be retrieved after creation.
                    </p>
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={creatingAdmin || !generatedPassword}
                    className="flex items-center gap-2 px-5 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50">
                    {creatingAdmin ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Create Admin Account
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="divide-y divide-gray-50">
            {adminsLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : adminUsers.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">No admin users found.</div>
            ) : (
              adminUsers.map(admin => (
                <div key={admin.id} className="px-6 py-4 flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full bg-[#40E0D0]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-[#40E0D0]">
                      {(admin.first_name || admin.email)[0]?.toUpperCase() || 'A'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {admin.first_name || admin.last_name ? `${admin.first_name} ${admin.last_name}`.trim() : 'Admin User'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{admin.email}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {admin.id === user?.id && (
                      <span className="text-xs bg-[#40E0D0]/10 text-[#40E0D0] font-semibold px-2 py-0.5 rounded-full">You</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${admin.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {admin.is_active ? 'Active' : 'Inactive'}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(admin.granted_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Login Security ── */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Login Security & Access Logs</h2>
              <p className="text-xs text-gray-500">Monitor admin login activity, IP addresses, and security alerts</p>
            </div>
          </div>
          <div className="p-6">
            <LoginSecurityTab userType="admin" />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function AdminUsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
